import { useState } from "react";
import {
  DownloadSimple,
  FunnelSimple,
  Info,
  MagnifyingGlass,
  UsersThree,
  X,
} from "@phosphor-icons/react";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { exportUsers } from "@/api/users";
import { UsersTable } from "@/components";
import { useDebounce, useSearch, useUsers } from "@/hooks";
import { ContentLayout } from "@/layout";
import { ROUTES } from "@/router";
import { Breadcrumb, Button, Heading, MultiSelect, SectionHeader } from "@/ui";
import { tw } from "@/utils";

const inputClass =
  "block w-full rounded-xl border border-neutral-400 p-3 text-sm text-neutral-950 placeholder:text-neutral-400 hover:ring-1 hover:ring-tertiary-600 focus:shadow-input focus:outline-none focus:ring-1 focus:ring-tertiary-600";

const labelClass =
  "text-xs font-semibold uppercase tracking-wide text-neutral-500";

const PLAN_OPTIONS = [
  { label: "All Users", value: "" },
  { label: "Courses", value: "courses" },
  { label: "Trainings", value: "trainings" },
  { label: "Practices (Monthly)", value: "practices_monthly" },
  { label: "Practices (Yearly)", value: "practices_yearly" },
  { label: "Self Paced", value: "self_paced" },
];

const PLAN_FILTER_OPTIONS = [
  { value: "courses", label: "Courses" },
  { value: "trainings", label: "Trainings" },
  { value: "practices_monthly", label: "Practices (Monthly)" },
  { value: "practices_yearly", label: "Practices (Yearly)" },
  { value: "self_paced", label: "Self Paced" },
];

export const Users = () => {
  const navigate = useNavigate();
  const { searchQuery, handleSearchChange, setSearchQuery } = useSearch("");
  const [exportPlan, setExportPlan] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);

  const { useUserCountries } = useUsers();
  const { data: countries } = useUserCountries();
  const countryOptions = (countries ?? []).map((country) => ({
    value: country,
    label: country,
  }));

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportUsers(exportPlan || undefined);
    } finally {
      setIsExporting(false);
    }
  };

  const { watch, register, reset } = useForm({
    defaultValues: {
      statusQuery: "",
      roleQuery: "",
      hasPartnerQuery: "",
      registeredFrom: "",
      registeredTo: "",
    },
  });

  const statusQuery = watch("statusQuery");
  const roleQuery = watch("roleQuery");
  const hasPartnerQuery = watch("hasPartnerQuery");
  const registeredFrom = watch("registeredFrom");
  const registeredTo = watch("registeredTo");

  const debouncedSearch = useDebounce(searchQuery);

  const activeFilterCount = [
    selectedCountries.length > 0,
    selectedPlans.length > 0,
    !!roleQuery,
    !!statusQuery,
    !!hasPartnerQuery,
    !!registeredFrom || !!registeredTo,
  ].filter(Boolean).length;

  const hasActiveFilters = !!searchQuery || activeFilterCount > 0;

  const clearAll = () => {
    setSearchQuery("");
    setSelectedCountries([]);
    setSelectedPlans([]);
    reset({
      statusQuery: "",
      roleQuery: "",
      hasPartnerQuery: "",
      registeredFrom: "",
      registeredTo: "",
    });
  };

  const breadcrumbItems = [
    {
      label: t("general.users"),
      href: ROUTES.partners,
      icon: <UsersThree />,
    },
  ];

  return (
    <ContentLayout className="flex flex-col gap-y-6">
      <Breadcrumb
        items={breadcrumbItems}
        current={t("general.users")}
        onItemClick={(item) => {
          if (item.href) {
            navigate(item.href);
          }
        }}
      />
      <SectionHeader
        left={<Heading level={3} heading={t("general.users")} />}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={exportPlan}
              onChange={(e) => setExportPlan(e.target.value)}
              className={tw(inputClass, "w-auto")}
            >
              {PLAN_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Button
              variant="outlined"
              onClick={() => void handleExport()}
              disabled={isExporting}
            >
              <DownloadSimple size={16} />
              {isExporting ? "Exporting..." : "Export CSV"}
            </Button>
          </div>
        }
      />

      {/* Search + Filters toggle */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex w-full flex-row items-center sm:max-w-md">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlass
                size={20}
                className={tw(
                  "text-neutral-950",
                  !searchQuery && "text-neutral-500",
                )}
              />
            </div>
            <input
              type="text"
              id="search"
              placeholder={t("users.search_placeholder")}
              value={searchQuery}
              onChange={handleSearchChange}
              className={tw(inputClass, "pl-10")}
            />
          </div>

          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            className={tw(
              "flex items-center gap-2 rounded-xl border border-neutral-400 bg-white p-3 text-sm font-medium text-neutral-950 hover:bg-neutral-50",
              filtersOpen && "border-primary-600 bg-primary-50",
            )}
            aria-expanded={filtersOpen}
          >
            <FunnelSimple size={18} />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-700 px-1.5 text-xs font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAll}
              className="flex items-center gap-1 text-sm font-semibold text-primary-700 hover:text-primary-900 sm:ml-auto"
            >
              <X size={16} />
              Clear all
            </button>
          )}
        </div>

        {/* Filters panel */}
        {filtersOpen && (
          <div className="grid grid-cols-1 gap-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Country</span>
              <MultiSelect
                placeholder={
                  selectedCountries.length
                    ? `${selectedCountries.length} selected`
                    : "Select countries"
                }
                options={countryOptions}
                selectedOptions={selectedCountries}
                onChange={setSelectedCountries}
                containerClassName="w-full"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Plan</span>
              <MultiSelect
                placeholder={
                  selectedPlans.length
                    ? `${selectedPlans.length} selected`
                    : "Select plans"
                }
                options={PLAN_FILTER_OPTIONS}
                selectedOptions={selectedPlans}
                onChange={setSelectedPlans}
                containerClassName="w-full"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Role</span>
              <select className={inputClass} {...register("roleQuery")}>
                <option value="">All roles</option>
                <option value="admin">Admin</option>
                <option value="partner">Partner</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Status</span>
              <select className={inputClass} {...register("statusQuery")}>
                <option value="">All statuses</option>
                <option value="0">Active</option>
                <option value="1">Locked</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Partner</span>
              <select className={inputClass} {...register("hasPartnerQuery")}>
                <option value="">All users</option>
                <option value="1">Has a partner</option>
                <option value="0">No partner</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Registered</span>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className={inputClass}
                  aria-label="Registered from"
                  {...register("registeredFrom")}
                />
                <span className="text-sm text-neutral-500">to</span>
                <input
                  type="date"
                  className={inputClass}
                  aria-label="Registered to"
                  {...register("registeredTo")}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plain-English legend for the access columns */}
      <div className="flex items-start gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-2.5 text-sm text-neutral-600">
        <Info size={16} className="mt-0.5 shrink-0 text-neutral-400" />
        <p className="leading-snug">
          <span className="font-semibold text-neutral-800">Subscriptions</span>{" "}
          lists each app plan the person has, tagged{" "}
          <span className="font-semibold text-success-700">Paid</span> (they
          purchased it) or{" "}
          <span className="font-semibold text-warning-700">Free</span> (gifted,
          e.g. via a partner).{" "}
          <span className="font-semibold text-neutral-800">
            Materials &amp; courses
          </span>{" "}
          shows how many individual courses or material bundles have been
          granted to this person (separate from their app subscription).
        </p>
      </div>

      <UsersTable
        searchQuery={debouncedSearch}
        locked={statusQuery}
        filters={{
          country: selectedCountries,
          role: roleQuery,
          plan: selectedPlans,
          hasPartner: hasPartnerQuery,
          registeredFrom,
          registeredTo,
        }}
      />
    </ContentLayout>
  );
};
